import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ThumbnailList } from '../ThumbnailList';
import { Download } from 'lucide-react';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../Accordion';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';
import { Button } from '../Button';

const StudyItem = ({
  date,
  description,
  numInstances,
  modalities,
  isActive,
  onClick,
  isExpanded,
  displaySets,
  activeDisplaySetInstanceUIDs,
  onClickThumbnail,
  onDoubleClickThumbnail,
  onClickUntrack,
  viewPreset = 'thumbnails',
  ThumbnailMenuItems,
  StudyMenuItems,
  StudyInstanceUID,
  patientName,
  servicesManager,
}: withAppTypes) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const uiNotificationService = servicesManager?.services?.uiNotificationService;

  const handleDownload = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (isDownloading) return;

    const pacsApiUrl = (window as any).config?.pacsApiUrl;
    if (!pacsApiUrl || !StudyInstanceUID) return;

    try {
      setIsDownloading(true);

      // Mostrar notificación de progreso
      if (uiNotificationService) {
        uiNotificationService.show({
          title: 'Descarga de estudio',
          message: 'El estudio está siendo procesado para su descarga, esto podría demorar algunos segundos.',
          type: 'info',
          duration: 5000,
        });
      }

      const response = await fetch(`${pacsApiUrl}/api/studies/zip/${StudyInstanceUID}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Usar patientName si está disponible, sino usar StudyInstanceUID
      const fileName = patientName ? `${patientName}.zip` : `${StudyInstanceUID}.zip`;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar el estudio:', error);

      // Mostrar notificación de error
      if (uiNotificationService) {
        uiNotificationService.show({
          title: 'Error en la descarga',
          message: 'No se pudo descargar el archivo. Por favor, intente nuevamente.',
          type: 'error',
          duration: 5000,
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Accordion
      type="single"
      collapsible
      onClick={onClick}
      onKeyDown={() => {}}
      role="button"
      tabIndex={0}
      defaultValue={isActive ? 'study-item' : undefined}
    >
      <AccordionItem value="study-item">
        <AccordionTrigger className={classnames('hover:bg-accent bg-popover group w-full rounded')}>
          <div className="flex h-[40px] w-full flex-row overflow-hidden">
            <div className="flex w-full flex-row items-center justify-between">
              <div className="flex min-w-0 flex-row items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onClick={handleDownload}
                      disabled={isDownloading}
                    >
                      <Download className={classnames('h-4 w-4', isDownloading && 'animate-pulse')} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isDownloading ? 'Descargando...' : 'Descargar estudio'}
                  </TooltipContent>
                </Tooltip>
                <div className="flex min-w-0 flex-col items-start text-[13px]">
                  <Tooltip>
                    <TooltipContent>{date}</TooltipContent>
                    <TooltipTrigger className="w-full">
                      <div className="h-[18px] w-full max-w-[160px] overflow-hidden truncate whitespace-nowrap text-left text-white">
                        {date}
                      </div>
                    </TooltipTrigger>
                  </Tooltip>
                  <Tooltip>
                    <TooltipContent>{description}</TooltipContent>
                    <TooltipTrigger className="w-full">
                      <div className="text-muted-foreground h-[18px] w-full overflow-hidden truncate whitespace-nowrap text-left">
                        {description}
                      </div>
                    </TooltipTrigger>
                  </Tooltip>
                </div>
              </div>
              <div className="text-muted-foreground flex flex-col items-end pl-[10px] text-[12px]">
                <div className="max-w-[150px] overflow-hidden text-ellipsis">{modalities}</div>
                <div>{numInstances}</div>
              </div>
              {StudyMenuItems && (
                <div className="ml-2 flex items-center">
                  <StudyMenuItems StudyInstanceUID={StudyInstanceUID} />
                </div>
              )}
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent
          onClick={event => {
            event.stopPropagation();
          }}
        >
          {isExpanded && displaySets && (
            <ThumbnailList
              thumbnails={displaySets}
              activeDisplaySetInstanceUIDs={activeDisplaySetInstanceUIDs}
              onThumbnailClick={onClickThumbnail}
              onThumbnailDoubleClick={onDoubleClickThumbnail}
              onClickUntrack={onClickUntrack}
              viewPreset={viewPreset}
              ThumbnailMenuItems={ThumbnailMenuItems}
            />
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

StudyItem.propTypes = {
  date: PropTypes.string.isRequired,
  description: PropTypes.string,
  modalities: PropTypes.string.isRequired,
  numInstances: PropTypes.number.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
  displaySets: PropTypes.array,
  activeDisplaySetInstanceUIDs: PropTypes.array,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  viewPreset: PropTypes.string,
  StudyMenuItems: PropTypes.func,
  StudyInstanceUID: PropTypes.string,
  patientName: PropTypes.string,
  servicesManager: PropTypes.object,
};

export { StudyItem };
